/**
 * Version Checker - Ensures users get fresh data after updates
 *
 * Clears old localStorage data when app version changes
 * Prevents users from seeing stale demo data or old UI
 */

import { logger } from './logger';

// Increment this version when you deploy breaking changes
const APP_VERSION = '2.1.0'; // Updated October 10, 2025
const VERSION_KEY = 'repconnect_app_version';

export const versionChecker = {
  /**
   * Check if app version has changed and clear old data if needed
   * Call this on app initialization or login
   */
  checkAndUpdate: () => {
    const storedVersion = localStorage.getItem(VERSION_KEY);

    if (storedVersion !== APP_VERSION) {
      logger.debug(`🔄 App version changed from ${storedVersion || 'unknown'} to ${APP_VERSION}`);
      logger.debug('🧹 Clearing old localStorage data...');

      // List of localStorage keys to preserve (authentication and settings)
      const keysToPreserve = [
        'repconnect-auth-token',
        'repconnect_current_user',
        'sb-cjypamlmoyulzpxydefc-auth-token', // Supabase auth
        'pwa-install-dismissed',
        'ios-install-dismissed'
      ];

      // Get all keys
      const allKeys = Object.keys(localStorage);

      // Remove everything except preserved keys
      allKeys.forEach(key => {
        if (!keysToPreserve.includes(key)) {
          logger.debug(`  Removing old data: ${key}`);
          localStorage.removeItem(key);
        }
      });

      // Update stored version
      localStorage.setItem(VERSION_KEY, APP_VERSION);
      logger.debug('✅ localStorage cleaned for new version');

      return true; // Indicates cleanup happened
    }

    return false; // No cleanup needed
  },

  /**
   * Force clear all localStorage (for debugging or manual reset)
   */
  forceReset: () => {
    logger.debug('⚠️ Force resetting all localStorage data...');
    localStorage.clear();
    localStorage.setItem(VERSION_KEY, APP_VERSION);
    logger.debug('✅ All localStorage cleared');
    window.location.reload();
  },

  /**
   * Get current app version
   */
  getVersion: () => APP_VERSION,

  /**
   * Check if service worker needs update
   */
  checkServiceWorkerUpdate: async () => {
    if ('serviceWorker' in navigator) {
      const registration = await navigator.serviceWorker.ready;

      // Check for updates
      await registration.update();

      // If there's a waiting service worker, activate it
      if (registration.waiting) {
        logger.debug('📱 New service worker waiting, activating...');
        registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        return true;
      }
    }
    return false;
  }
};

export default versionChecker;
